import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient';
import { toast } from 'react-hot-toast';
import logoImg from '../../assets/img/logo-cgciparis.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  
  const { resetPassword } = useAuth(); // Nous n'utilisons plus signIn car nous utilisons directement supabase
  const navigate = useNavigate();
  const location = useLocation();
  
  // Récupérer l'URL de redirection après connexion
  const from = location.state?.from?.pathname || '/admin';

  // Vérifier si l'utilisateur est déjà connecté
  useEffect(() => {
    const checkAuth = async () => {
      try {
        console.log('Login: Vérification de l\'authentification');
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          console.log('Login: Utilisateur déjà connecté, redirection vers', from);
          navigate(from, { replace: true });
        }
      } catch (error) {
        console.error('Login: Erreur lors de la vérification de l\'authentification:', error);
      }
    };
    
    checkAuth();
  }, [navigate, from]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setIsLoading(true);
    console.log('Login: Tentative de connexion avec', email);
    
    try {
      // Utiliser directement Supabase pour la connexion
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        console.error('Login: Erreur de connexion:', error.message);
        throw error;
      }
      
      if (data && data.user) {
        console.log('Login: Connexion réussie, redirection vers', from);
        navigate(from, { replace: true });
      } else {
        console.error('Login: Connexion échouée, pas d\'utilisateur retourné');
        toast.error('Échec de la connexion. Vérifiez vos identifiants.');
      }
    } catch (error) {
      console.error('Login: Erreur de connexion:', error);
      toast.error(error.message || 'Une erreur est survenue lors de la connexion');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      toast.error('Veuillez entrer votre adresse email');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const result = await resetPassword(resetEmail);
      
      if (result.success) {
        setResetSent(true);
        toast.success(result.message || 'Instructions envoyées à votre email');
      } else {
        toast.error(result.error || 'Échec de l\'envoi des instructions');
      }
    } catch (error) {
      console.error('Erreur de réinitialisation:', error);
      toast.error('Une erreur est survenue lors de la réinitialisation');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div className="flex flex-col items-center">
          <img src={logoImg} alt="CGCI Paris Logo" className="h-24 mb-4" />
          <h2 className="mt-3 text-center text-3xl font-extrabold text-gray-900">
            {showForgotPassword ? 'Réinitialisation du mot de passe' : 'Connexion à l\'administration'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {showForgotPassword 
              ? 'Entrez votre email pour recevoir les instructions' 
              : 'Veuillez vous connecter pour accéder à l\'administration'}
          </p>
        </div>
        
        {!showForgotPassword ? (
          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email-address" className="sr-only">Adresse email</label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
                  placeholder="Adresse email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">Mot de passe</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
                  placeholder="Mot de passe"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <button
                  type="button"
                  className="font-medium text-orange-600 hover:text-orange-500"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Mot de passe oublié?
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${
                  isLoading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-colors duration-200 shadow-md`}
              >
                {isLoading ? (
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <svg className="animate-spin h-5 w-5 text-orange-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </span>
                ) : null}
                Se connecter
              </button>
            </div>
          </form>
        ) : (
          <div className="mt-8 space-y-6">
            {resetSent ? (
              <div className="bg-green-50 p-4 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">Instructions envoyées</h3>
                    <div className="mt-2 text-sm text-green-700">
                      <p>Veuillez vérifier votre boîte de réception pour les instructions de réinitialisation de mot de passe.</p>
                    </div>
                    <div className="mt-4">
                      <button
                        type="button"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200 shadow-sm"
                        onClick={() => {
                          setShowForgotPassword(false);
                          setResetSent(false);
                        }}
                      >
                        Retour à la connexion
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleResetPassword}>
                <div className="rounded-md shadow-sm -space-y-px">
                  <div>
                    <label htmlFor="reset-email" className="sr-only">Adresse email</label>
                    <input
                      id="reset-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-orange-500 focus:border-orange-500 focus:z-10 sm:text-sm"
                      placeholder="Adresse email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="mt-6 flex items-center justify-between">
                  <button
                    type="button"
                    className="text-sm font-medium text-gray-600 hover:text-gray-500 transition-colors duration-200"
                    onClick={() => setShowForgotPassword(false)}
                  >
                    Retour à la connexion
                  </button>
                  
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`inline-flex justify-center py-2 px-5 border border-transparent shadow-md text-sm font-medium rounded-md text-white ${
                      isLoading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'
                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500`}
                  >
                    {isLoading ? 'Envoi en cours...' : 'Envoyer les instructions'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
